import React from "react";
import { connect } from "react-redux";
import { Form, Col, Row } from "react-bootstrap";
import PropTypes from "prop-types";
import { v4 as uuidv4 } from "uuid";
import { NumericFormat as NumberFormat } from "react-number-format";

import ConditionalButton from "../../../layout/conditional-button";
import ErrorModal from "../../../layout/modal";
import DeleteModal from "../../../layout/modal";
import { SettingListSelect } from "../../../layout/select/setting-list";

import { isGuid } from "../../../utils/string";
import { withNavigation } from "../../../utils/wrapper";

import { send as pageInfo, loading } from "../../../actions/page";
import { get as getRate, save as saveRate, destroy } from "../../../actions/rates";

const STYLE_VALUES = ["H", "C", "F"];

class Detail extends React.Component {
    constructor(props) {
        super(props);
        this.props.loading(true);
        this.state = {
            ...this.mapPropsToState(props),
            modal: {
                error: { show: false, title: "", body: "" },
                delete: { show: false, title: "", body: "" }
            }
        };
        this.props.pageInfo({ title: "Rate", breadcrumb: `[New Rate]` });
        this.changes = {};
    }

    mapPropsToState = (props) => {
        const { uuid, name, code, style, value, index } = props;
        const styleIndex = STYLE_VALUES.indexOf(style);
        return {
            uuid: uuid || null,
            name: name || "",
            code: code || "",
            style: STYLE_VALUES.includes(style) ? style : STYLE_VALUES[0],
            value: value ?? null,
            index: index ?? null,
            styleIndex: styleIndex >= 0 ? styleIndex : 0
        };
    };

    get uuid() {
        return this.props.params?.uuid;
    }

    loadData = async () => {
        this.props.loading(true);
        try {
            if (isGuid(this.uuid)) {
                const rate = await this.props.getRate({ uuid: this.uuid });

                if (!rate) {
                    this.setState(prev => ({
                        ...prev,
                        modal: {
                            ...prev.modal,
                            error: {
                                show: true,
                                title: "Rate not found",
                                body: "The selected rate could not be loaded."
                            }
                        }
                    }));
                    return;
                }

                this.setState({
                    ...this.mapPropsToState(rate)
                });

                await this.props.pageInfo({
                    title: `Rate (${rate.name} [${rate.code}])`,
                    breadcrumb: `${rate.name} [${rate.code}]`
                });
            }
        } catch (err) {
            this.setState(prev => ({
                ...prev,
                modal: {
                    ...prev.modal,
                    error: {
                        show: true,
                        title: "Load error",
                        body: err?.message || "Unable to load the rate."
                    }
                }
            }));
        } finally {
            this.props.loading(false);
        }
    };

    async componentDidMount() {
        await this.loadData();
    }

    async componentDidUpdate(prevProps) {
        if (prevProps.params?.uuid !== this.props.params?.uuid) {
            await this.loadData();
        }
    }

    doPostChange = () => {
        if (this.props.onChange) this.props.onChange({ ...this.state });
    };

    onChange = (e) => {
        this.setState(
            {
                [e.target.name]: e.target.value
            },
            this.doPostChange
        );
    };

    onValueChange = (value) => {
        this.onChange({ target: { name: "value", value } });
    };

    onStyleChange = (e) => {
        const idx = e?.target?.index ?? 0;
        this.setState(
            {
                style: STYLE_VALUES[idx] || STYLE_VALUES[0],
                styleIndex: idx
            },
            this.doPostChange
        );
    };

    onSaveClick = async () => {
        this.props.loading(true);
        try {
            let { code, name, index, value, style } = this.state;
            const payload = {
                uuid: isGuid(this.uuid) ? this.uuid : uuidv4(),
                code,
                name,
                index,
                value,
                style
            };

            const rate = await this.props.saveRate(payload);

            if (!rate?.uuid) {
                throw new Error("Rate was not saved correctly.");
            }

            this.props.navigate(`/dashboard/settings/rates/${rate.uuid}`);
        } catch (err) {
            this.setState(prev => ({
                ...prev,
                modal: {
                    ...prev.modal,
                    error: {
                        show: true,
                        title: "Save error",
                        body: err?.message || "Unable to save this rate."
                    }
                }
            }));
        } finally {
            this.props.loading(false);
        }
    };

    onDeleteClick = () => {
        this.setState(prev => ({
            modal: {
                ...prev.modal,
                delete: {
                    title: "Delete rate",
                    body: `This action will permanently delete the rate named ${this.state.name}. If this rate is in use, blanks or zeroes may appear. Proceed with caution.`,
                    show: true
                }
            }
        }));
    };

    onDeleteOkModal = async (e) => {
        this.props.loading(true);
        try {
            const targetUuid = isGuid(this.uuid) ? this.uuid : this.state.uuid;
            if (!targetUuid) throw new Error("No valid rate UUID found for delete.");

            await this.props.destroy(targetUuid);
            this.onDeleteHideModal(e);
            this.props.navigate("/dashboard/settings/rates");
        } catch (err) {
            this.setState(prev => ({
                ...prev,
                modal: {
                    ...prev.modal,
                    error: {
                        show: true,
                        title: "Delete error",
                        body: err?.message || "Unable to delete this rate."
                    }
                }
            }));
        } finally {
            this.props.loading(false);
        }
    };

    onDeleteHideModal = () => {
        this.setState(prev => ({
            modal: {
                ...prev.modal,
                delete: { ...prev.modal.delete, show: false }
            }
        }));
    };

    onErrorHideModal = () => {
        this.setState(prev => ({
            modal: {
                ...prev.modal,
                error: { ...prev.modal.error, show: false }
            }
        }));
    };

    render() {
        return (
            <>
                <Col md="8" className="float-right text-end">
                    <ConditionalButton
                        enabledVariant="outline-primary"
                        tooltip="Save Rate"
                        display={true}
                        enabled={true}
                        onEnabledClick={this.onSaveClick}
                        icon="save"
                    >
                        Save
                    </ConditionalButton>
                    <ConditionalButton
                        enabledVariant="outline-danger"
                        tooltip="Delete rate"
                        display={true}
                        enabled={true}
                        onEnabledClick={this.onDeleteClick}
                        icon="bin"
                    >
                        Delete
                    </ConditionalButton>
                </Col>

                <Col xs="12">
                    <Row>
                        <Col xs="12" md="8" xl="5">
                            <div className="mb-3 top-label">
                                <Form.Control
                                    type="text"
                                    name="name"
                                    value={this.state.name || ""}
                                    onChange={this.onChange}
                                />
                                <Form.Label>NAME</Form.Label>
                            </div>
                        </Col>

                        <Col xs="12" md="4" xl="3">
                            <div className="mb-3 top-label">
                                <Form.Control
                                    type="text"
                                    name="code"
                                    value={this.state.code || ""}
                                    onChange={this.onChange}
                                />
                                <Form.Label>CODE</Form.Label>
                            </div>
                        </Col>

                        <Col xs="12" xl="4">
                            <div className="mb-3 top-label">
                                <SettingListSelect
                                    name="style"
                                    id="style"
                                    index={this.state.styleIndex}
                                    indexed
                                    onChange={this.onStyleChange}
                                    items={["Frequency", "Contingency", "Flat"]}
                                />
                                <Form.Label>STYLE</Form.Label>
                            </div>
                        </Col>

                        <Col xs="12" md="5">
                            <div className="mb-3 top-label">
                                <NumberFormat
                                    name="value"
                                    id="value"
                                    value={this.state.value ?? ""}
                                    thousandSeparator
                                    allowNegative={false}
                                    inputMode="decimal"
                                    decimalScale={2}
                                    prefix={this.state.styleIndex === 1 ? "" : "$"}
                                    suffix={this.state.styleIndex !== 1 ? "" : "%"}
                                    className="text-end form-control mb-2"
                                    isAllowed={({ floatValue, value }) =>
                                        value === "" ||
                                        (floatValue ?? 0) <=
                                            (this.state.styleIndex === 1 ? 100 : 500000)
                                    }
                                    onValueChange={({ floatValue, value }) => {
                                        this.onValueChange(value === "" ? null : (floatValue ?? null));
                                    }}
                                />
                                <Form.Label>AMOUNT</Form.Label>
                            </div>
                        </Col>
                    </Row>
                </Col>

                <ErrorModal
                    show={this.state.modal.error.show}
                    title={this.state.modal.error.title}
                    body={this.state.modal.error.body}
                    buttons={[
                        {
                            default: true,
                            caption: "ok",
                            onEnabledClick: this.onErrorHideModal,
                            variant: "secondary",
                            display: true,
                            enabled: true
                        }
                    ]}
                />

                <DeleteModal
                    show={this.state.modal.delete.show}
                    title={this.state.modal.delete.title}
                    body={this.state.modal.delete.body}
                    buttons={[
                        {
                            caption: "ok",
                            onEnabledClick: this.onDeleteOkModal,
                            variant: "primary",
                            display: true,
                            enabled: true
                        },
                        {
                            default: true,
                            caption: "cancel",
                            onEnabledClick: this.onDeleteHideModal,
                            variant: "secondary",
                            display: true,
                            enabled: true
                        }
                    ]}
                />
            </>
        );
    }
}

Detail.propTypes = {
    auth: PropTypes.object.isRequired
};

const mapStateToProps = (state) => ({
    auth: state.auth
});

export default withNavigation(
    connect(mapStateToProps, { pageInfo, loading, getRate, saveRate, destroy })(Detail)
);
